import * as FS from '@fullstory/browser';
import { init } from '@fullstory/browser';
import { useEffect } from 'react';

/** Debug Mode
 * When in debug mode FullStory will force itself to be enabled and will
 * console.log all events that are fired.
 */
function isDebugMode() {
  return !!window.DEBUG;
}

const isProduction = () => {
  return process.env.NODE_ENV === 'production';
};
const isStaging = () => {
  var _window, _window$location, _window$location$orig;

  return (_window = window) === null || _window === void 0 ? void 0 : (_window$location = _window.location) === null || _window$location === void 0 ? void 0 : (_window$location$orig = _window$location.origin) === null || _window$location$orig === void 0 ? void 0 : _window$location$orig.includes('zapier-staging');
};
const isProductionAndNotStaging = () => {
  return isProduction() && !isStaging();
};

// email addresses used in monolith e2e tests
const AUTOMATION_EMAIL_ADDRESSES = ['user@example.com', 'end-to-end@zapier-test.com'];
const IGNORED_DOMAINS = ['zapier.com', 'zapier.rodeo'];

const isIgnoredEmail = email => {
  var _email$split;

  if (!email) {
    return false;
  } // ignore email addresses known to be for e2e tests


  if (AUTOMATION_EMAIL_ADDRESSES.includes(email)) {
    return true;
  } // ignore email address from certain domains


  const domain = email === null || email === void 0 ? void 0 : (_email$split = email.split('@')) === null || _email$split === void 0 ? void 0 : _email$split[1];

  if (domain && IGNORED_DOMAINS.includes(domain)) {
    return true;
  }

  return false;
};

// - window is undefined (server environment)
// - this is not a production environment
// - email is included and matches ignore properties

const isDisabledMode = email => {
  // We don't invoke methods server-side, because FullStory only works client-side
  if (typeof window === 'undefined') {
    return true;
  }

  return !isProductionAndNotStaging() || isIgnoredEmail(email || '');
};

const FullStoryAPI = (method, ...args) => {
  var _args$;

  // We don't invoke methods server-side, because FullStory only works client-side
  if (typeof window === 'undefined') {
    return;
  }

  const email = (args === null || args === void 0 ? void 0 : (_args$ = args[0]) === null || _args$ === void 0 ? void 0 : _args$.email) || ''; // We want to make it easy for devs to debug their FullStory
  // calls, so we log the call to console.

  if (isDebugMode()) {
    // eslint-disable-next-line no-console
    console.log("\u2139\uFE0F (Debug mode) Invoked FullStory method \"".concat(method, "\" with arguments:"), ...args); // If !isDebugMode() then check for disabled mode and skip initialization
  } else if (isDisabledMode(email)) {
    // eslint-disable-next-line no-console
    console.log("\u2139\uFE0F (Disabled mode) Would have invoked FullStory method \"".concat(method, "\" with arguments:"), ...args);
    return;
  } // window.FS will be `undefined` when FullStory is uninitialized.
  // FS initialization is most commonly done via the `./hooks/useInstallFullStoryHook` React hook.
  // We want to prevent calling FullStory methods when uninitialized - return undefined and do not execute FS method.
  // If FS is initialized, execute FS method below.


  if (!window.FS || !window._fs_initialized) {
    return;
  } // @ts-ignore


  return FS[method](...args); // eslint-disable-line consistent-return
};

const ZAPIER_FULLSTORY_ORG = '1XM';
/**
 * Installs FullStory in the app. Will skip installing if
 * isDisabledMode() returns true AND isDebugMode() returns false
 */

function useInstallFullStory(options) {
  const namespace = options === null || options === void 0 ? void 0 : options.namespace;
  const isDisabled = (options === null || options === void 0 ? void 0 : options.isDisabled) || false;
  const email = (options === null || options === void 0 ? void 0 : options.email) || ''; // Init fullstory whenever namespace changes

  useEffect(() => {
    if ((isDisabled || isDisabledMode(email)) && !isDebugMode()) {
      return;
    }

    let isInIframe;

    try {
      isInIframe = window.top !== window.self;
    } catch (e) {
      isInIframe = true;
    }

    init({
      orgId: ZAPIER_FULLSTORY_ORG,
      namespace: namespace || 'FS',
      // Record the contents of this iFrame when embedded in a parent site
      // (for example, if we're in a partner's site that embeds Zapier)
      recordOnlyThisIFrame: isInIframe
    });
  }, [namespace, isDisabled, email]);
}

// Only cookies that start with the following allowed cookies
// will be logged as a user var
const ALLOWED_COOKIES = ['__tnp', 'APPMANAGEMENT_FE_SERVICE', 'IDENTITY_FE_SERVICE', 'currentAccountId', '__canary']; // Takes a cookie string and parses it into a dictionary

const parseCookies = (cookieStr = '') => {
  return cookieStr.split(' ').reduce((acc, cookie) => {
    const kv = cookie.replace(';', '').split('='); // only add the cookie if it's on the allowlist

    ALLOWED_COOKIES.forEach(allowed => {
      if (kv[0].startsWith(allowed)) {
        // acc the cookie as a key/value
        acc[kv[0]] = kv[1];
      }
    });
    return acc;
  }, {});
};

/**
 * Identifies the current session to FullStory, so you can cross-reference and segment sessions by Zapier user.
 * This hook gracefully handles being passed a falsey value, for when your user data is still loading.
 * @param user User data needed to identify the current Zapier session to FullStory.
 */

const useIdentifyToFullStory = user => {
  const {
    id,
    email,
    ...otherKeys
  } = user || {}; // Identify the user when the id changes

  useEffect(() => {
    if (id) {
      FullStoryAPI('identify', id);
    }
  }, [id]); // Log user cookies (allowlisted) so we can track canary release status

  useEffect(() => {
    const cookieStr = typeof document !== undefined ? document.cookie : '';
    const cookies = JSON.stringify(parseCookies(cookieStr));
    FullStoryAPI('setUserVars', {
      // Log the cookies
      cookies
    });
  }, []); // Email is the most common var to include besides id, so keep it in a dedicated useEffect

  useEffect(() => {
    if (email) {
      FullStoryAPI('setUserVars', {
        email,
        // Set the e-mail as the displayName, so they are used in FS UI as the user name
        displayName: email
      });
    }
  }, [email]); // update user vars if the keys change

  useEffect(() => {
    const hasOtherKeys = Object.keys(otherKeys) // Filter out `__typename`, a common key in graphql query response, but that we don't care about.
    // This isn't strictly necessary, but helps improve API ergonomics, so you can pass a query result
    // directly into the hook:
    // ```
    // const { data = {} } = useQuery(/* ... */);
    // useIdentifyToFullStory(data.currentUserV2); // will include `__typename` key
    // ```
    .filter(k => k !== '__typename').length > 0;

    if (hasOtherKeys) {
      FullStoryAPI('setUserVars', otherKeys);
    }
    /**
     * Can't just pass `otherKeys` into the dependency array here because
     * it's a new object each time (even if the values are the same), so this hook
     * will get called on every re-render. So we use `JSON.stringify` instead.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [JSON.stringify(otherKeys)]);
};

/**  https://help.fullstory.com/hc/en-us/articles/360020623274-FS-event-API-Sending-custom-event-data-into-FullStory */

function useSendFullStoryEvent(eventName, eventProperties = {}) {
  useEffect(() => {
    FullStoryAPI('event', eventName, eventProperties);
    /**
     * Can't just pass `eventProperties` into the dependency array here because
     * it's a new object each time (even if the values are the same), so this hook
     * will get called on every re-render.
     *
     * A deep compare of `eventProperties` is an option, but Dan Abramov says that
     * deep compares are generally a bad idea here, which is why `useEffect` doesn't
     * just do that itself. He suggests using `JSON.stringify` instead, as long as the
     * object is only a couple layers deep.
     *
     * https://twitter.com/dan_abramov/status/1104414272753487872
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventName, JSON.stringify(eventProperties)]);
}

/**  https://help.fullstory.com/hc/en-us/articles/360020623274-FS-event-API-Sending-custom-event-data-into-FullStory */

function useMaybeSendFullStoryEvent(shouldSendEvent, eventName, eventProperties = {}) {
  useEffect(() => {
    if (shouldSendEvent) {
      FullStoryAPI('event', eventName, eventProperties);
    }
    /**
     * Can't just pass `eventProperties` into the dependency array here because
     * it's a new object each time (even if the values are the same), so this hook
     * will get called on every re-render.
     *
     * A deep compare of `eventProperties` is an option, but Dan Abramov says that
     * deep compares are generally a bad idea here, which is why `useEffect` doesn't
     * just do that itself. He suggests using `JSON.stringify` instead, as long as the
     * object is only a couple layers deep.
     *
     * https://twitter.com/dan_abramov/status/1104414272753487872
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [shouldSendEvent, eventName, JSON.stringify(eventProperties)]);
}

export { FullStoryAPI, useIdentifyToFullStory, useInstallFullStory, useMaybeSendFullStoryEvent, useSendFullStoryEvent };
